/**
 * The words for a planned day — what the clock says about each stop, and what
 * to tell a customer whose time can't be kept.
 *
 * Owner, Oct 5 2026, on his Field page: "I want the optimize and re route
 * button to actually work — mario has in between 7-10 but stuck at 7". The
 * plan he was looking at called a visit set BETWEEN 7 and 10 "⏰ 7:00 AM · 176
 * min late" and made the 10:00, 11:00 and 12:00 late behind it. "176 min late"
 * tells him nothing he can act on from the truck. What he needs is which
 * times are still kept, a window shown as a window, and for the ones that
 * can't be kept: the new time, and who to call.
 *
 * Pure — no React, no network — so the Field page's plan panel, its stop
 * cards and the calendar's route preview say the same thing in the same words.
 */

/**
 * "14:05" → "2:05 PM" — the ONE 12-hour clock for a planned day (the Field
 * page, its plan panel, the bidder's Today).
 *
 * A plan that runs past midnight comes off the server as "24:16" (the stored
 * format; it sorts and subtracts like any other time). Each screen used to
 * format it for itself with `h % 12` and "PM" for anything not under 12, so a
 * day planned at 6:40 PM read "done about 2:43 PM" for a finish at 2:43 the
 * next morning, with stops at "12:16 PM" and "1:22 PM" in between (owner, Oct
 * 5 2026: "I want the optimize and re route button to actually work" — a time
 * that cannot be real is not working). Past midnight says so:
 * "24:16" → "12:16 AM tomorrow".
 */
export function clock12(t?: string | null): string {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m) || h < 0 || m < 0) return '';
  const hour = h % 24;
  return `${hour % 12 || 12}:${String(m).padStart(2, '0')} ${hour < 12 ? 'AM' : 'PM'}${h >= 24 ? ' tomorrow' : ''}`;
}
const mins = (t?: string | null): number | null => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t || ''));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
const hhmm = (n: number) => `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(Math.round(n % 60)).padStart(2, '0')}`;

/** A "between" visit's hours as the customer was told them — either end may be open. */
export interface PlanWindow { earliest?: string | null; latest?: string | null }

// "07:00" → 7 AM, "07:30" → 7:30 AM: a window reads short, the way he says it.
const shortClock = (t: string): { t: string; ap: string } | null => {
  const n = mins(t);
  if (n == null) return null;
  const h = Math.floor(n / 60) % 24, m = n % 60;
  return { t: `${h % 12 || 12}${m ? `:${String(m).padStart(2, '0')}` : ''}`, ap: h < 12 ? 'AM' : 'PM' };
};

/** "10:00" → "10 AM", "07:30" → "7:30 AM" — a window's end the way he says it. */
export function shortTime(t?: string | null): string {
  const c = t ? shortClock(t) : null;
  return c ? `${c.t} ${c.ap}` : '';
}

/** "7–10 AM" · "11 AM–1 PM" · "7:30–10 AM" · "after 7 AM" · "by 10 AM". '' when there is no window. */
export function windowWords(w?: PlanWindow | null): string {
  const a = w?.earliest ? shortClock(w.earliest) : null, b = w?.latest ? shortClock(w.latest) : null;
  if (a && b) return a.ap === b.ap ? `${a.t}–${b.t} ${b.ap}` : `${a.t} ${a.ap}–${b.t} ${b.ap}`;
  if (a) return `after ${a.t} ${a.ap}`;
  if (b) return `by ${b.t} ${b.ap}`;
  return '';
}
/** On a stop card: "Between 7–10 AM" · "After 7 AM" · "By 10 AM". */
export function betweenWords(w?: PlanWindow | null): string {
  const words = windowWords(w);
  if (!words) return '';
  return w?.earliest && w?.latest ? `Between ${words}` : words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * A quote visit's `hours` (GET /quotes/visits: exact / earliest / latest in
 * minutes, or any) → what the visit is promised: an exact time, a window, or
 * neither. The same rule the planner reads (server quoteSlots.hoursOfVisit).
 */
export function promiseOfHours(hours: any): { must_start: string | null; window: PlanWindow | null } {
  if (!hours || hours.any) return { must_start: null, window: null };
  if (hours.exact != null) return { must_start: hhmm(Number(hours.exact)), window: null };
  if (hours.earliest == null && hours.latest == null) return { must_start: null, window: null };
  return { must_start: null, window: { earliest: hours.earliest != null ? hhmm(Number(hours.earliest)) : null, latest: hours.latest != null ? hhmm(Number(hours.latest)) : null } };
}

// A set time missed by an hour is a missed appointment, not a late one — the
// same line the planner draws (server/services/dayRoute MISS_CAP).
const MISSED_AFTER_MIN = 60;
// A quote visit when nothing says how long (the owner's setting; 20 by default).
const VISIT_MIN = 20;

export type ClockTone = 'kept' | 'behind' | 'late' | 'moved' | 'anytime';
export interface ClockWords { text: string; tone: ClockTone }
export interface ClockStop {
  must_start?: string | null; window?: PlanWindow | null; eta?: string | null;
  /** when the visit ends ('HH:MM'), or how long it takes — for a window that is reached in time but not finished in time */
  done?: string | null; minutes?: number | null;
  late_minutes?: number | null; passed?: boolean; gone_by?: boolean; moved?: boolean;
  /** a few minutes behind a set time — kept, not late (the server's rule: dayRoute.LATE_OK) */
  behind_minutes?: number | null;
  /** a "between" visit he reaches inside its hours that will run past the end of them — kept, amber */
  over_minutes?: number | null;
}

/**
 * One stop on the clock:
 *   exact time kept          "⏰ 10:00 AM"
 *   window kept              "⏰ 7–10 AM · there ~9:05 AM"
 *   kept, a few minutes off  "⏰ 2:00 PM · about 2 min behind" — amber, never red: a
 *                            couple of minutes on an estimated drive is not a broken promise
 *   kept in place but late   "⏰ 4:00 PM · there ~4:24 PM"
 *   window reached in its    "⏰ 7–10 AM · done ~10:16 AM" in AMBER — he gets there at 9:56,
 *   hours, finished after    inside what the customer was told, so it is kept (over_minutes);
 *                            the amber only says the visit runs past ten
 *   gone by                  "was 7–10 AM · now ~2:05 PM" / "was 7:00 AM · now ~2:05 PM"
 *   can't be kept, but its   "⏰ 11:15 AM · there ~4:26 PM" — it has not gone by, so it is
 *   time is still ahead      not "was": at 9:27 AM a time he had set a minute before read
 *                            "was 11:15 AM · now ~4:26 PM"
 *   no set time              "~1:34 PM"
 * Never "176 min late": for a time that has gone by, the number that matters
 * is when he will actually be there.
 *
 * Everything here — `late_minutes`, `behind_minutes`, `passed`, `moved` — comes
 * from the server's plan, on every day, saved or not. This used to work the
 * lateness out a second time for a saved order read back off the jobs (and
 * guessed "gone by" at an hour); a saved day now asks the same clock an
 * unsaved one does, so there is one answer and nothing is worked out here.
 */
export function clockWords(p: ClockStop): ClockWords {
  const exact = mins(p.must_start), eta = mins(p.eta);
  const win = exact == null ? windowWords(p.window) : '';
  if (exact == null && !win) return { text: p.eta ? `~${clock12(p.eta)}` : 'Anytime', tone: 'anytime' };
  const promised = exact != null ? clock12(p.must_start) : win;
  const latest = mins(p.window?.latest);
  const late = Number(p.late_minutes) || 0;
  if (!late || eta == null) {
    const behind = Math.round(Number(p.behind_minutes) || 0);
    if (behind > 0) return { text: `⏰ ${promised} · about ${behind} min behind`, tone: 'behind' };
    // Inside the window when he arrives, past it when he leaves: kept — she was told "between", and he is
    // there — but worth a glance, so the time shown is when the visit ends.
    if (win && eta != null && Number(p.over_minutes) > 0) {
      const done = mins(p.done) ?? eta + (Number(p.minutes) > 0 ? Number(p.minutes) : VISIT_MIN);
      return { text: `⏰ ${promised} · done ~${clock12(hhmm(done))}`, tone: 'behind' };
    }
    // A window says when inside it he will be there; an exact time needs no second number.
    return { text: `⏰ ${promised}${win && eta != null ? ` · there ~${clock12(p.eta)}` : ''}`, tone: 'kept' };
  }
  // "was … now" is for a time that is behind him: gone by on the real clock,
  // or before the start he picked. One the planner had to move while it is
  // still ahead is a time that will be missed, said the plain way, in red.
  if (p.passed || p.gone_by) {
    return { text: `was ${promised} · now ~${clock12(p.eta)}`, tone: 'moved' };
  }
  if (p.moved) return { text: `⏰ ${promised} · there ~${clock12(p.eta)}`, tone: 'late' };
  // Inside the window when he arrives, past it when he leaves: the time that is wrong is when it ends.
  if (win && latest != null && eta <= latest) {
    const done = mins(p.done) ?? eta + (Number(p.minutes) > 0 ? Number(p.minutes) : VISIT_MIN);
    return { text: `⏰ ${promised} · done ~${clock12(hhmm(done))}`, tone: 'late' };
  }
  return { text: `⏰ ${promised} · there ~${clock12(p.eta)}`, tone: 'late' };
}

/**
 * A route preview that already leads each line with the arrival time
 * (Calendar → route a day): what was promised, and whether it holds.
 *   "promised 10:00 AM" · "promised 2:00 PM — about 3 min behind" ·
 *   "promised 4:00 PM — about 24 min late" · "promised 7:00 AM — can't be kept"
 * An hour or more behind is a missed visit, not a late one, so no minute
 * count — and so is a time that has already gone by, or one the planner had
 * to move out of the way to keep the others (the sheet now plans with the
 * Field page's planner, which does that). A few minutes behind is kept, in
 * amber: the server's rule (dayRoute.LATE_OK) — `late_minutes` arrives as 0
 * for it and `behind_minutes` says by how much. Nothing is worked out here.
 */
export function promisedWords(p: { must_start?: string | null; late_minutes?: number | null; behind_minutes?: number | null; gone_by?: boolean; passed?: boolean; moved?: boolean }): ClockWords | null {
  if (!p.must_start) return null;
  const late = Math.max(0, Math.round(Number(p.late_minutes) || 0));
  const promised = `promised ${clock12(p.must_start)}`;
  if (!late) {
    const behind = Math.max(0, Math.round(Number(p.behind_minutes) || 0));
    return behind ? { text: `${promised} — about ${behind} min behind`, tone: 'behind' } : { text: promised, tone: 'kept' };
  }
  if (late >= MISSED_AFTER_MIN || p.gone_by || p.passed || p.moved) return { text: `${promised} — can't be kept`, tone: 'moved' };
  return { text: `${promised} — about ${late} min late`, tone: 'late' };
}

/** "24 min" · "1½ hours" · "6 hours" — past an hour and a half nobody counts minutes. */
export function lateWords(minutes: number): string {
  const n = Math.max(0, Math.round(Number(minutes) || 0));
  if (n < 90) return `${n} min`;
  const halves = Math.round(n / 30);
  return `${Math.floor(halves / 2) || ''}${halves % 2 ? '½' : ''} hours`;
}

/** "a 16-minute drive", "an 18-minute drive": the article the number is said with (eight, eleven, eighteen, eighty…). */
export function aOrAn(n: number): string {
  const v = Math.abs(Math.round(Number(n) || 0));
  return v === 8 || v === 11 || v === 18 || (v >= 80 && v <= 89) || (v >= 800 && v <= 899) ? 'an' : 'a';
}

/**
 * The one line above a saved order when the day is no longer the day he
 * planned: "The day changed — 2 stops aren't in your order, 1 set time is now
 * late." '' when nothing is news. The counts are the server's (POST
 * /jobs/day-order `changed`): a stop added since he saved, and a set time that
 * was kept in the plan he saved and is late now. A late stop he already saw
 * and saved anyway is not "the day changed" — it stays in the paragraphs below.
 * long_wait: a stop he would now sit outside for a long while that he did not
 * when he saved (he moved a visit to 7:30 PM, and the order had him parked
 * there from 3:55 with nothing said).
 */
export function dayChangedWords(changed?: { not_in_order?: number | null; now_late?: number | null; long_wait?: number | null } | null): string {
  const n = Math.max(0, Math.round(Number(changed?.not_in_order) || 0)), m = Math.max(0, Math.round(Number(changed?.now_late) || 0));
  const w = Math.max(0, Math.round(Number(changed?.long_wait) || 0));
  const parts = [
    n ? (n === 1 ? "1 stop isn't in your order" : `${n} stops aren't in your order`) : '',
    m ? (m === 1 ? '1 set time is now late' : `${m} set times are now late`) : '',
    w ? (w === 1 ? '1 stop now has a long wait' : `${w} stops now have a long wait`) : '',
  ].filter(Boolean);
  return parts.length ? `The day changed — ${parts.join(', ')}.` : '';
}

/**
 * Stops the plan only reaches after his day ends (Settings → "Your day ends
 * at"; the server lists them in `after_end`): "3 stops land after 9 PM and
 * won't happen today: Dwayne Pruitt (~9:46 PM), …". Nothing is moved for him —
 * the page only says so. '' when the day fits.
 */
export function afterEndWords(plan: { day_end?: string | null; after_end?: { customer_name?: string | null; eta?: string | null }[] | null } | null | undefined, isToday: boolean): string {
  const list = plan?.after_end || [];
  if (!list.length || !plan?.day_end) return '';
  const names = list.map(e => `${e.customer_name || 'A stop'}${e.eta ? ` (~${clock12(e.eta)})` : ''}`).join(', ');
  const day = isToday ? 'today' : 'that day';
  return list.length === 1
    ? `1 stop lands after ${shortTime(plan.day_end)} and won't happen ${day}: ${names}.`
    : `${list.length} stops land after ${shortTime(plan.day_end)} and won't happen ${day}: ${names}.`;
}

export const firstName = (name?: string | null): string => String(name || '').trim().split(/\s+/)[0] || '';

/**
 * Where a plan leaves from, the way it reads after "from": "home" ·
 * "Mario Delgado's" · "Pick up mulch" in quotes for a to-do. '' when the plan
 * does not say.
 *
 * Owner, Oct 5 2026: "I want the optimize and re route button to actually
 * work". A plan made mid-day used to start at his house; now it starts at the
 * last stop he finished, and the page has to say which — "a 16-minute drive
 * from where the day starts" was a sentence he could not decode.
 */
export function startPlace(plan?: { from?: string | null; from_job_id?: number | null; from_todo?: boolean } | null): string {
  if (!plan || !plan.from) return '';
  if (plan.from_job_id == null) return 'home';
  return plan.from_todo ? `"${plan.from}"` : `${plan.from}'s`;
}

// (The sms: link that opened his own Messages app is gone — every office text goes through the thread on Texts,
// utils/textsLink.)
export const telHref = (phone: string): string => `tel:${String(phone || '').replace(/[^\d+]/g, '')}`;

/**
 * What the text says. Today it is "running behind"; for a day planned ahead it
 * is a change of time — and so it is today when he has only chosen to start
 * later and the time has not come yet (`behind: false`): he is not behind,
 * he is changing the plan. `was`: the time they were told, already in words
 * ("10:00 AM", "between 7–10 AM").
 */
export function newTimeText(o: { customer: string; me: string; company: string; eta: string; was?: string | null; isToday: boolean; dayWords: string; behind?: boolean }): string {
  const hi = firstName(o.customer) ? `Hi ${firstName(o.customer)}, ` : 'Hi, ';
  const who = `this is ${firstName(o.me) || 'the owner'} with ${o.company}.`;
  const when = clock12(o.eta);
  if (o.isToday && o.behind !== false) return `${hi}${who} I'm running behind today — I can be there about ${when}. Does that still work for you?`;
  return `${hi}${who} About your visit ${o.isToday ? 'today' : `on ${o.dayWords}`} — I can be there about ${when}${o.was ? ` instead of ${o.was}` : ''}. Does that still work for you?`;
}

/**
 * Where the plan has him sitting: "You wait about 44 min before Carla Mendez."
 * Owner, Oct 5 2026: "I want the optimize and re route button to actually
 * work" — a plan could park him outside a set time for most of an hour and no
 * row said so; he only saw two arrival times a long way apart. Waits of
 * twenty minutes or more, from the server's plan (`wait_minutes`). '' when
 * there are none.
 */
export function waitWords(plan?: { stops?: { fixed?: boolean; customer_name?: string | null; wait_minutes?: number | null }[] | null } | null): string {
  const waits = (plan?.stops || []).filter(s => !s.fixed && Number(s.wait_minutes) >= 20);
  if (!waits.length) return '';
  return `You wait ${waits.map(s => `about ${lateWords(Number(s.wait_minutes))} before ${s.customer_name || 'the next stop'}`).join(', and ')}.`;
}
